import { useEffect, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { AppInstellingen } from '@/lib/api-types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Info, Loader2, Megaphone, Save } from 'lucide-react';
import { toast } from 'sonner';

/**
 * De melding die de klantomgeving bovenin toont.
 *
 * Eén tekst voor iedereen, aan of uit. Hij komt terecht boven het opnamescherm, de
 * instellingen en de handleiding -- zie components/InfoBanner.tsx.
 *
 * De tekst en de schakelaar staan los van elkaar, en dat is te zien in dit scherm:
 * uitzetten laat de tekst staan. Een onderhoudsmelding die elk kwartaal terugkomt
 * hoeft zo niet elke keer opnieuw getypt te worden.
 */

/** Dezelfde grens als in settingsSchema op de server. */
const MAX_TEKENS = 1000;

export function NoticeTab() {
  const [tekst, setTekst] = useState('');
  const [actief, setActief] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const { settings } = await api.get<{ settings: AppInstellingen | null }>('/admin/settings');
        if (settings) {
          setTekst(settings.noticeMessage ?? '');
          setActief(settings.noticeActive ?? false);
        }
      } catch (fout) {
        toast.error(fout instanceof ApiFout ? fout.message : 'Kon de melding niet laden');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    const opgeschoond = tekst.trim();

    /**
     * Aan zonder tekst kan de database wel aan, maar het levert een banner op die
     * niets toont -- en dan staat er in dit scherm "aan" terwijl er bij de
     * gebruikers niets verschijnt. Dat verschil is hier goedkoper te voorkomen dan
     * later te verklaren.
     */
    if (actief && opgeschoond === '') {
      toast.error('Vul eerst een tekst in, of zet de melding uit.');
      return;
    }

    setIsSaving(true);
    try {
      await api.patch('/admin/settings', {
        noticeMessage: opgeschoond,
        noticeActive: actief,
      });
      setTekst(opgeschoond);
      toast.success(actief ? 'De melding staat aan' : 'De melding staat uit');
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Opslaan mislukt');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Megaphone className="h-5 w-5" />
          Melding
        </CardTitle>
        <CardDescription>
          Staat de melding aan, dan ziet iedere gebruiker deze tekst bovenin de
          klantomgeving. Een geopend scherm pikt een wijziging binnen vijf minuten op.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="melding-tekst" className="text-sm">
            Tekst
          </Label>
          <Textarea
            id="melding-tekst"
            rows={3}
            maxLength={MAX_TEKENS}
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
            placeholder="Bijvoorbeeld: Op donderdag 9 oktober tussen 19:00 en 21:00 is de spraakherkenning korte tijd niet beschikbaar."
          />
          <p className="text-xs text-muted-foreground">
            {tekst.length} / {MAX_TEKENS} tekens. Platte tekst; opmaak en links worden als
            tekst getoond.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 border rounded-lg p-4">
          <div className="space-y-0.5">
            <Label htmlFor="melding-actief" className="text-sm">
              Melding tonen
            </Label>
            <p className="text-xs text-muted-foreground">
              Uitzetten laat de tekst staan voor een volgende keer.
            </p>
          </div>
          <Switch id="melding-actief" checked={actief} onCheckedChange={setActief} />
        </div>

        {/*
          De voorvertoning is met opzet dezelfde opmaak als InfoBanner en geen
          import ervan: dat component haalt de opgeslagen melding op, en zou hier dus
          tonen wat er stáát in plaats van wat er komt te staan.
        */}
        <div className="space-y-1.5">
          <Label className="text-sm">Voorvertoning</Label>
          {tekst.trim() === '' ? (
            <p className="text-xs text-muted-foreground">Nog geen tekst om te tonen.</p>
          ) : (
            <div className="flex items-start gap-2 px-3 py-2 bg-info/10 border border-info/30 rounded-md">
              <Info className="h-4 w-4 text-info shrink-0 mt-px" aria-hidden="true" />
              <p className="text-xs leading-relaxed whitespace-pre-line">{tekst.trim()}</p>
            </div>
          )}
        </div>

        <Button onClick={() => void handleSave()} disabled={isSaving} size="sm">
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Opslaan
        </Button>
      </CardContent>
    </Card>
  );
}
